import React, { useState, useEffect, useContext } from 'react';
import EditDailyBudgetModal from './EditDailyBudgetModal';
import TableSkeleton from 'src/components/common/ui/loader/TableSkeleton';
import ToggleSwitch from 'src/components/common/ui/ToggleSwitch';
import { useHttp, useNotification } from 'src/hooks';
import { AuthContext } from 'src/contexts';
import { useNavigate } from 'react-router-dom';

interface CampaignData {
  id: string;
  name: string;
  status: string;
  objective: string;
  insights: {
    dailyBudget: string;
    results: string;
    costPerResult: string;
    totalSpent: string;
    costPerClick: string;
    avgHookRate: string;
    avgHoldRate: string;
  };
}

interface CampaignTableProps {
  data: CampaignData[];
  isLoading: boolean;
  onStatusChange?: () => void; // Add this prop to refresh campaigns after status change
}

const CampaignTable: React.FC<CampaignTableProps> = ({ data, isLoading, onStatusChange }) => {
  const { sendRequest } = useHttp();
  const notify = useNotification();
  const { accessToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);
  const [toggleStates, setToggleStates] = useState<{ [key: string]: boolean }>({});

  // Initialize toggle states based on campaign status
  useEffect(() => {
    const initialToggleStates: { [key: string]: boolean } = {};
    data.forEach(item => {
      initialToggleStates[item.id] = item.status === 'ACTIVE';
    });
    setToggleStates(initialToggleStates);
  }, [data]);

  const handleOpenModal = (budget: string) => {
    setSelectedBudget(budget);
    setIsModalOpen(true);
  };

  const handleSaveBudget = (newBudget: string) => {
    console.log('Updated Budget:', newBudget);
    setIsModalOpen(false);
  };

  const updateCampaignStatus = async (campaignId: string, newStatus: 'ACTIVE' | 'PAUSED') => {
    // Find the campaign in the data to check for adSetId and adId
    const campaign = data.find(item => item.id === campaignId);

    // Prepare the payload
    const payload: any = {
      campaignId,
      status: newStatus,
    };

    // Add adSetId and adId if they exist in the first ad (assuming one ad per campaign)
    if (campaign?.insights?.ads?.length > 0) {
      const firstAd = campaign?.insights.ads[0];
      if (firstAd.adSetId) {
        payload.adSetId = firstAd.adSetId;
      }
      if (firstAd.adId) {
        payload.adId = firstAd.adId;
      }
    }

    // Optimistic UI update
    setToggleStates(prev => ({
      ...prev,
      [campaignId]: newStatus === 'ACTIVE',
    }));

    await sendRequest(
      {
        url: `campaign/status`,
        method: 'PATCH',
        accessToken,
        payload, // Send the complete payload
      },
      (data: any) => {
        notify(`Campaign status updated to ${newStatus}`, 'success');
        if (onStatusChange) onStatusChange();
      },
    );
  };

  const handleToggle = (id: string, isOn: boolean) => {
    const newStatus = isOn ? 'ACTIVE' : 'PAUSED';
    updateCampaignStatus(id, newStatus);
  };
  if (isLoading) {
    return <TableSkeleton />;
  }

  const handleSeeCreatives = (campaignId: string) => {
    console.log(campaignId);
    navigate(`/dashboard/campaign-performance/${campaignId}`);
  };
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="min-w-full text-sm text-left text-gray-500">
        <thead className="text-sm font-normal font-inter text-[#4A4D4F]">
          <tr>
            <th className="px-6 py-4">Generated leads</th>
            <th className="px-6 py-4">Daily budget</th>
            <th className="px-6 py-4">Results</th>
            <th className="px-6 py-4">Cost per result</th>
            <th className="px-6 py-4">Total spent</th>
            <th className="px-6 py-4">Cost per click</th>
            <th className="px-6 py-4">No. of creatives</th>
            <th className="px-6 py-4">Avg. hook rate</th>
            <th className="px-6 py-4">Avg. hold rate</th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => (
            <tr key={item.id} className="border-t bg-white hover:bg-gray-50 text-black">
              <td className="px-6 py-4 flex items-center space-x-3">
                {/* <img
                  src="https://via.placeholder.com/40"
                  alt="Lead Thumbnail"
                  className="w-10 h-10 rounded-full"
                /> */}
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-indigo-600 font-medium">
                    {item.name
                      .split(' ')
                      .slice(0, 2)
                      .map(word => word[0])
                      .join('')
                      .toUpperCase()
                      .substring(0, 2)}{' '}
                  </span>
                </div>
                <div>
                  <p className="text-black font-medium">{item.name}</p>
                  <p className="text-gray-400 text-xs">{item.id}</p>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <span
                    className="text-indigo-500 cursor-pointer"
                    onClick={() => handleOpenModal(item.insights.dailyBudget)}
                  >
                    {item.insights.dailyBudget}
                  </span>
                  <ToggleSwitch
                    isOn={toggleStates[item.id] || false}
                    onToggle={isOn => handleToggle(item.id, isOn)}
                  />
                </div>
              </td>
              <td className="px-6 py-4">{item.insights.results}</td>
              <td className="px-6 py-4">{item.insights.costPerResult}</td>
              <td className="px-6 py-4">{item.insights.totalSpent}</td>
              <td className="px-6 py-4">{item.insights.costPerClick}</td>
              <td className="px-6 py-4">
                <span
                  className="text-indigo-500 text-sm cursor-pointer hover:underline"
                  onClick={() => handleSeeCreatives(item.id)}
                >
                  see creatives
                </span>
              </td>
              <td className="px-6 py-4">{item.insights.avgHookRate}</td>
              <td className="px-6 py-4">{item.insights.avgHoldRate}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <EditDailyBudgetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialBudget={selectedBudget || ''}
        onSave={handleSaveBudget}
      />
    </div>
  );
};

export default CampaignTable;
